import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MapPin, RefreshCw, CheckCircle2, AlertTriangle, X } from 'lucide-react';

interface RefreshLokasiProps {
  targetName: string;
  targetLat: number;
  targetLng: number;
  radius: number;
  geofenceType?: 'radius' | 'polygon';
  polygonCoordinates?: { lat: number; lng: number }[];
  calculateDistance: (lat1: number, lng1: number, lat2: number, lng2: number) => number;
  isPointInPolygon: (point: { lat: number; lng: number }, polygon: { lat: number; lng: number }[]) => boolean;
  onConfirm: (coords: { lat: number; lng: number }, distance: number, accuracy: number) => void;
  onCancel: () => void;
}

// Akurasi GPS dianggap "baik" jika radius kesalahan <= nilai ini (meter)
const GOOD_ACCURACY_THRESHOLD = 25;
// Setelah menunggu selama ini tanpa hasil bagus, tawarkan opsi "tetap lanjutkan"
const ACCEPTABLE_WAIT_MS = 20000;

type ReadingStatus = 'searching' | 'good' | 'poor' | 'error';

export const PortalGuruModalRefreshLokasi: React.FC<RefreshLokasiProps> = ({
  targetName,
  targetLat,
  targetLng,
  radius,
  geofenceType,
  polygonCoordinates,
  calculateDistance,
  isPointInPolygon,
  onConfirm,
  onCancel,
}) => {
  const [status, setStatus] = useState<ReadingStatus>('searching');
  const [currentAccuracy, setCurrentAccuracy] = useState<number | null>(null);
  const [currentDistance, setCurrentDistance] = useState<number | null>(null);
  const [bestReading, setBestReading] = useState<{ lat: number; lng: number; accuracy: number; distance: number } | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');

  const watchIdRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(Date.now());
  const elapsedIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isWithinTarget = useCallback((lat: number, lng: number, dist: number, accuracy: number) => {
    if (geofenceType === 'polygon' && polygonCoordinates && polygonCoordinates.length > 2) {
      return isPointInPolygon({ lat, lng }, polygonCoordinates);
    }
    // Add accuracy buffer
    const buffer = Math.min(accuracy, GOOD_ACCURACY_THRESHOLD);
    return dist <= (radius + buffer);
  }, [geofenceType, polygonCoordinates, radius, isPointInPolygon]);

  const startWatch = useCallback(() => {
    if (!navigator.geolocation) {
      setStatus('error');
      setErrorMsg('Browser Anda tidak mendukung Geolocation.');
      return;
    }

    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
    }

    setStatus('searching');
    setErrorMsg('');
    startTimeRef.current = Date.now();
    setElapsedMs(0);
    setAttempt(prev => prev + 1);

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        const dist = calculateDistance(latitude, longitude, targetLat, targetLng);

        setCurrentAccuracy(accuracy);
        setCurrentDistance(dist);

        setBestReading(prev => {
          if (!prev || accuracy < prev.accuracy) {
            return { lat: latitude, lng: longitude, accuracy, distance: dist };
          }
          return prev;
        });

        const withinTarget = isWithinTarget(latitude, longitude, dist, accuracy);

        if (accuracy <= GOOD_ACCURACY_THRESHOLD) {
          setStatus('good');
          // Auto-lanjut begitu dapat pembacaan presisi & di dalam area — user tak perlu tap apa pun
          if (withinTarget) {
            if (watchIdRef.current !== null) navigator.geolocation.clearWatch(watchIdRef.current);
            onConfirm({ lat: latitude, lng: longitude }, dist, accuracy);
          }
        } else {
          setStatus('poor');
        }
      },
      (error) => {
        console.error(error);
        setStatus('error');
        if (error.code === error.PERMISSION_DENIED) {
          setErrorMsg('Izin lokasi ditolak. Aktifkan izin lokasi "Presisi/Precise" di pengaturan HP Anda.');
        } else if (error.code === error.TIMEOUT) {
          setErrorMsg('Waktu pencarian lokasi habis. Coba lagi atau pindah ke area terbuka.');
        } else {
          setErrorMsg('Gagal mengambil lokasi. Pastikan GPS aktif.');
        }
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  }, [calculateDistance, targetLat, targetLng, isWithinTarget, onConfirm]);

  useEffect(() => {
    startWatch();
    elapsedIntervalRef.current = setInterval(() => {
      setElapsedMs(Date.now() - startTimeRef.current);
    }, 500);

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      if (elapsedIntervalRef.current) clearInterval(elapsedIntervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleManualRetry = () => {
    setBestReading(null);
    startWatch();
  };

  const handleUseBestReading = () => {
    if (bestReading) {
      onConfirm({ lat: bestReading.lat, lng: bestReading.lng }, bestReading.distance, bestReading.accuracy);
    }
  };

  const canOfferForceUse = elapsedMs > ACCEPTABLE_WAIT_MS && bestReading !== null;
  const bestIsWithinTarget = bestReading ? isWithinTarget(bestReading.lat, bestReading.lng, bestReading.distance, bestReading.accuracy) : false;

  return (
    <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-sm p-6 space-y-4">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-lg font-bold text-gray-800">Mencari Lokasi Anda</h3>
            <p className="text-sm text-gray-500">Target: {targetName}</p>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <X size={22} />
          </button>
        </div>

        <div className="flex flex-col items-center py-4">
          <div className={`relative size-20 rounded-full flex items-center justify-center ${
            status === 'good' ? 'bg-green-100' : status === 'error' ? 'bg-red-100' : 'bg-amber-100'
          }`}>
            {status === 'searching' && <RefreshCw size={32} className="text-amber-500 animate-spin" />}
            {status === 'poor' && <MapPin size={32} className="text-amber-500 animate-pulse" />}
            {status === 'good' && <CheckCircle2 size={32} className="text-green-600" />}
            {status === 'error' && <AlertTriangle size={32} className="text-red-500" />}
          </div>

          <p className="mt-3 text-sm text-gray-600 text-center">
            {status === 'searching' && 'Sedang mencari sinyal GPS presisi...'}
            {status === 'poor' && 'Sinyal GPS kurang akurat, mencoba memperbaiki...'}
            {status === 'good' && !bestIsWithinTarget && 'Lokasi presisi didapat, tapi di luar area.'}
            {status === 'good' && bestIsWithinTarget && 'Lokasi ditemukan!'}
            {status === 'error' && errorMsg}
          </p>
        </div>

        {(currentAccuracy !== null || bestReading) && (
          <div className="bg-gray-50 rounded-2xl p-3 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-gray-500">Akurasi GPS saat ini</span>
              <span className={`font-semibold ${currentAccuracy && currentAccuracy <= GOOD_ACCURACY_THRESHOLD ? 'text-green-600' : 'text-amber-600'}`}>
                {currentAccuracy ? `± ${Math.round(currentAccuracy)}m` : '-'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Jarak ke lokasi</span>
              <span className="font-semibold text-gray-700">
                {currentDistance !== null ? `${Math.round(currentDistance)}m` : '-'}
              </span>
            </div>
            {bestReading && (
              <div className="flex justify-between pt-1 border-t border-gray-200 mt-1">
                <span className="text-gray-500">Akurasi terbaik</span>
                <span className="font-semibold text-gray-700">± {Math.round(bestReading.accuracy)}m</span>
              </div>
            )}
          </div>
        )}

        <div className="space-y-2 pt-2">
          <button
            onClick={handleManualRetry}
            className="w-full py-3 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium flex items-center justify-center gap-2"
          >
            <RefreshCw size={18} /> Refresh Lokasi (percobaan ke-{attempt + 1})
          </button>

          {canOfferForceUse && (
            <button
              onClick={handleUseBestReading}
              className={`w-full py-3 rounded-2xl font-medium ${
                bestIsWithinTarget
                  ? 'bg-[#40513B] hover:bg-[#334130] text-white'
                  : 'bg-amber-100 hover:bg-amber-200 text-amber-800'
              }`}
            >
              {bestIsWithinTarget ? 'Gunakan Lokasi Ini' : 'Tetap Lanjutkan dengan Lokasi Terbaik'}
            </button>
          )}

          <button onClick={onCancel} className="w-full py-3 text-gray-500 font-medium">
            Batal
          </button>
        </div>
      </div>
    </div>
  );
};
